import React, { useCallback, useMemo, useRef } from "react"
import Image from "next/image"

import { useUpdateableState } from "@lib"

import { CatCategory } from "@firmament-packages"
import { AffExpertProfile } from "@firmament-packages/aff"

import AccordionFilterOptions, { FilterOption } from "@components/AccordionFilterOptions"

const AffLessonFilters: React.FunctionComponent<{
    categories: CatCategory[],
    experts: AffExpertProfile[],
    selectedCategories?: CatCategory[],
    selectedExperts?: AffExpertProfile[],
    /**
     * If set to true, notification of filters selection changes only get
     * triggered when user clicks on Apply button.
     */
    cancelable?: boolean,
    onSelectedCategoriesChange?: (categories: CatCategory[]) => void,
    onSelectedProfilesChange?: (expertProfiles: AffExpertProfile[]) => void,
    onDismiss?: () => void
}> = ({
    categories,
    experts,
    selectedCategories,
    selectedExperts,
    cancelable,
    onSelectedCategoriesChange,
    onSelectedProfilesChange,
    onDismiss
}) => {
    const [selectedValues, setSelectedValues] = useUpdateableState(
        (selectedCategories || []).map((each) => each.id).concat((selectedExperts || []).map((each) => each.id)),
        (current, next) => {
            return (current || []).sort().join("|") !== (next || []).sort().join("|")
        }
    )

    const previousSelectedValues = useRef(
        (selectedCategories || [])
            .map((each) => each.id)
            .concat((selectedExperts || [])
            .map((each) => each.id))
    )

    const categoryOptionsByType = useMemo(() => {
        return categories.reduce((result: Record<string, FilterOption[]>, current: CatCategory) => {
            if (!result[current.categoryType!]) {
                result[current.categoryType!] = []
            }
            result[current.categoryType!].push({ title: current.title || "", value: current.id })
            return result
        }, {} as Record<string, FilterOption[]>)
    }, [categories])

    const selectedCategoryValuesByType = useMemo(() => {
        return categories.reduce((result: Record<string, string[]>, current: CatCategory) => {
            if (selectedValues?.includes(current.id) !== true) {
                return result
            }

            if (!result[current.categoryType!]) {
                result[current.categoryType!] = []
            }
            result[current.categoryType!].push(current.id)
            return result
        }, {} as Record<string, string[]>)
    }, [categories, selectedValues])

    const selectedExpertValues = useMemo(() => {
        return (experts || []).filter((each) => selectedValues?.includes(each.id) === true)
            .map((each) => each.id)
    }, [experts, selectedValues])

    const selectedCategoriesChangeHandler = useCallback((selectedOptionValues: string[], categoryType: string) => {
        const categoryIds = categories.filter((each) => each.categoryType === categoryType).map((each) => each.id)
        const nextSelectedValues = (selectedValues || [])
            .filter((each) => !categoryIds.includes(each))
            .concat(...selectedOptionValues)

        setSelectedValues(nextSelectedValues)
        onSelectedCategoriesChange && onSelectedCategoriesChange(
            categories.filter((each) => nextSelectedValues.includes(each.id))
        )
    }, [categories, selectedValues, setSelectedValues, onSelectedCategoriesChange])

    const selectedProfilesChangeHandler = useCallback((selectedOptionValues: string[]) => {
        const expertProfileIds = experts.map((each) => each.id)
        const nextSelectedValues = (selectedValues || [])
            .filter((each) => !expertProfileIds.includes(each))
            .concat(...selectedOptionValues)

        setSelectedValues(nextSelectedValues)
        onSelectedProfilesChange && onSelectedProfilesChange(
            experts.filter((each) => nextSelectedValues.includes(each.id))
        )
    }, [experts, selectedValues, setSelectedValues, onSelectedProfilesChange])

    const cancelButtonHandler = useCallback(() => {
        onSelectedCategoriesChange && onSelectedCategoriesChange(
            categories.filter((each) => (previousSelectedValues.current || []).includes(each.id))
        )
        onSelectedProfilesChange && onSelectedProfilesChange(
            experts.filter((each) => (previousSelectedValues.current || []).includes(each.id))
        )

        onDismiss && onDismiss()
    }, [categories, experts, previousSelectedValues, onSelectedCategoriesChange, onSelectedProfilesChange, onDismiss])

    const clearFilterHandler = useCallback(() => {
        if ((selectedValues || []).length === 0) {
            return
        }

        setSelectedValues([])

        onSelectedCategoriesChange && onSelectedCategoriesChange([])
        onSelectedProfilesChange && onSelectedProfilesChange([])
    }, [selectedValues, setSelectedValues, onSelectedCategoriesChange, onSelectedProfilesChange])

    return (
        <div className="bg-white-900 overflow-auto overscroll-contain">
            {cancelable &&
                <div className="flex p-[20px] bg-primary-100">
                    <h3 className="text-title2 text-black-500 flex-auto">Filters</h3>
                    <button type="button" className="w-[24px] h-[24px] my-auto"
                        onClick={cancelButtonHandler}>
                        <Image width={24} height={24} objectFit="contain" src="/images/ic_close_primary.svg" alt="Close Filters" />
                    </button>
                </div>
            }

            <AccordionFilterOptions
                title="Program"
                options={categoryOptionsByType["PROGRAM"]}
                values={selectedCategoryValuesByType["PROGRAM"]}
                onChange={(selected) => selectedCategoriesChangeHandler(selected, "PROGRAM")} />
            <hr className="border-black-100" />

            <AccordionFilterOptions
                title="Fitness Area"
                options={categoryOptionsByType["SUBJECT"]}
                values={selectedCategoryValuesByType["SUBJECT"]}
                onChange={(selected) => selectedCategoriesChangeHandler(selected, "SUBJECT")} />
            <hr className="border-black-100" />

            <AccordionFilterOptions
                title="Accessibility"
                options={categoryOptionsByType["ACCESSIBILITY"]}
                values={selectedCategoryValuesByType["ACCESSIBILITY"]}
                onChange={(selected) => selectedCategoriesChangeHandler(selected, "ACCESSIBILITY")} />
            <hr className="border-black-100" />

            <AccordionFilterOptions
                title="Intensity"
                options={categoryOptionsByType["INTENSITY"]}
                values={selectedCategoryValuesByType["INTENSITY"]}
                onChange={(selected) => selectedCategoriesChangeHandler(selected, "INTENSITY")} />
            <hr className="border-black-100" />

            <AccordionFilterOptions
                title="AFF Expert"
                options={experts.map((each) => ({ title: each.name || "", value: each.id }))}
                values={selectedExpertValues}
                onChange={selectedProfilesChangeHandler} />
            <hr className="border-black-100" />

            <div className={["flex flex-col gap-y-[16px]"].concat(cancelable || (selectedValues || []).length > 0 ? ["p-[20px]"] : []).join(" ")}>
                {cancelable && <button type="button" className="w-full px-[16px] py-[12px] border-[1px] bg-accent-900 rounded-[8px] text-callout text-white-900 text-center uppercase"
                    onClick={() => onDismiss && onDismiss()}>
                    Apply Filters
                </button>}

                {(selectedValues || []).length > 0 && <button type="button" className="w-full px-[16px] py-[12px] border-[1px] border-accent-900 rounded-[8px] hover:bg-accent-100 text-callout text-accent-900 text-center uppercase"
                    onClick={clearFilterHandler}>
                    Clear Filter
                </button>}
            </div>
        </div>
    )
}

export default AffLessonFilters
